import { createContext, useReducer, useContext } from 'react';
import authService from '../services/authService';
import googleAuthService from '../services/googleAuthService';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

// Get API URL from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Initial auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Auth action types
const AUTH_ACTIONS = {
  AUTH_INIT: 'AUTH_INIT',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_ERROR: 'AUTH_ERROR',
  AUTH_LOGOUT: 'AUTH_LOGOUT'
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.AUTH_INIT:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case AUTH_ACTIONS.AUTH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload,
        error: null
      };
    case AUTH_ACTIONS.AUTH_ERROR:
      // Ensure error is a string or Error object
      let errorValue = null;
      
      if (action.payload) {
        if (typeof action.payload === 'string') {
          errorValue = action.payload;
        } else if (action.payload instanceof Error) {
          errorValue = action.payload.message || 'Authentication error';
        } else if (typeof action.payload === 'object') {
          try {
            errorValue = action.payload.message || JSON.stringify(action.payload);
          } catch (e) {
            errorValue = 'Authentication error';
          }
        } else {
          errorValue = String(action.payload);
        }
      }

      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: errorValue
      };
    case AUTH_ACTIONS.AUTH_LOGOUT:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: null
      };
    default:
      return state;
  }
};

// Create the Auth context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Update this function to match your API structure
const fetchUserData = async (token) => {
  try {
    // Decode the token to get the user ID
    const decodedToken = jwtDecode(token);
    
    // Get user ID from token claims
    const userId = decodedToken.id || decodedToken.sub;
    
    if (!userId) {
      console.error('No user ID found in token');
      throw new Error('Invalid user token');
    }
    
    // Use the /:id endpoint instead of /me
    const response = await axios.get(`${API_URL}/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (response && response.data && response.data.success) {
      // Transform response based on the new API structure
      const userData = {
        id: userId,
        ...response.data.data,
        // Ensure these fields are always present with fallbacks
        name: response.data.data.name || decodedToken.name || decodedToken.email?.split('@')[0] || 'User',
        email: response.data.data.email || decodedToken.email || '',
        role: response.data.data.is_admin ? 'admin' : 'user',
        emailVerified: response.data.data.email_verified === 1,
        authProvider: response.data.data.auth_provider || 'local',
        is_admin: response.data.data.is_admin || false
      };
      
      return userData;
    } else {
      console.error('Invalid user data response format', response);
      
      // Fallback to using just token data if API doesn't return expected format
      return {
        id: userId,
        name: decodedToken.name || decodedToken.email?.split('@')[0] || 'User',
        email: decodedToken.email || '',
        role: decodedToken.role || 'user',
        emailVerified: decodedToken.emailVerified || false
      };
    }
  } catch (error) {
    console.error('Error fetching user data:', error);
    
    // Handle specific error scenarios
    if (error.response?.status === 404) {
      throw new Error('User profile not found');
    }
    
    if (error.response?.status === 401) {
      throw new Error('Unauthorized access to user profile');
    }
    
    // Fall back to using token data if API request fails
    try {
      const decodedToken = jwtDecode(token);
      return {
        id: decodedToken.id || decodedToken.sub,
        name: decodedToken.name || decodedToken.email?.split('@')[0] || 'User',
        email: decodedToken.email || '',
        role: decodedToken.role || 'user',
        emailVerified: decodedToken.emailVerified || true // Assume verified if we got a token
      };
    } catch (tokenError) {
      console.error('Failed to extract user data from token:', tokenError);
      throw new Error('Failed to get user profile');
    }
  }
};

// Make sure these functions are exposed through the provider value
export const AuthProvider = ({ children }) => {
  // State and reducer setup
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { isAuthenticated, user, isLoading, error } = state;

  // Add the checkAuth function to verify token validity
  const checkAuth = async () => {
    try {
      // Check if we have a token
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }
      
      // Verify it's not expired by decoding
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        // Check if token is expired
        if (decodedToken.exp < currentTime) {
          console.log('Token expired');
          localStorage.removeItem('token');
          return false;
        }
        
        // If we already have user data in state, consider auth valid
        if (isAuthenticated && user) {
          return true;
        }
        
        // No user data in state, try to fetch user profile with token
        try {
          // Set token in the auth header for API calls
          authService.setAuthToken(token);
          
          // Fetch user data
          const userData = await fetchUserData(token);
          
          // Update auth state with user data
          dispatch({ 
            type: AUTH_ACTIONS.AUTH_SUCCESS,
            payload: userData
          });
          
          return true;
        } catch (fetchError) {
          console.error('Error fetching user data:', fetchError);
          return false;
        }
      } catch (decodeError) {
        console.error('Invalid token:', decodeError);
        localStorage.removeItem('token');
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      return false;
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.AUTH_INIT });
      
      // Use the actual authService to perform login
      const response = await authService.login(email, password);
      
      // Log response for debugging
      console.log('Login response:', response);
      
      // Validate response structure based on the sample response
      // {
      //   "success": true,
      //   "message": "Login successful",
      //   "user": { id, name, email, picture, emailVerified, authProvider },
      //   "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
      // }
      
      if (!response || !response.success || !response.token) {
        throw new Error('No authentication token received');
      }
      
      // Get user data from response
      const userData = response.user;
      
      if (!userData || !userData.id) {
        throw new Error('User data is missing from the response');
      }
      
      // Check if email is verified (emailVerified can be boolean true or number 1)
      if (userData.emailVerified !== true && userData.emailVerified !== 1) {
        throw new Error(JSON.stringify({
          code: 'EMAIL_NOT_VERIFIED',
          message: 'Please verify your email address before logging in.',
          user: userData
        }));
      }
      
      // Create standardized user object
      const user = {
        id: userData.id,
        name: userData.name || email.split('@')[0],
        email: userData.email || email,
        picture: userData.picture,
        role: userData.role || 'user',
        emailVerified: true, // We already checked above
        authProvider: userData.authProvider || 'local'
      };
      
      // Store token in localStorage
      localStorage.setItem('token', response.token);
      
      // Set token for API requests
      authService.setAuthToken(response.token);
      
      // Update auth state
      dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: user });
      
      // Return success with token for redirection logic
      return { 
        success: true, 
        user,
        token: response.token,
        message: response.message || 'Login successful'
      };
    } catch (error) {
      console.error('Login error:', error);
      
      // Clear any auth state on error
      localStorage.removeItem('token');
      authService.removeAuthToken();
      
      // Check if the error is already in JSON format
      if (typeof error.message === 'string' && error.message.startsWith('{')) {
        try {
          const parsedError = JSON.parse(error.message);
          
          // Update auth state with error (except for specific redirect cases)
          if (parsedError.code !== 'EMAIL_NOT_VERIFIED') {
            dispatch({ 
              type: AUTH_ACTIONS.AUTH_ERROR, 
              payload: parsedError.message || 'Login failed'
            });
          }
          
          // Re-throw the parsed error
          throw parsedError;
        } catch (parseError) {
          // If parsing fails, continue with original error
          console.log('Error parsing error message:', parseError);
        }
      }
      
      // Create a structured error response
      const errorResponse = {
        success: false,
        code: error.code || 'LOGIN_ERROR',
        message: error.message || 'Login failed. Please try again.'
      };
      
      dispatch({ 
        type: AUTH_ACTIONS.AUTH_ERROR, 
        payload: errorResponse.message
      });
      
      throw errorResponse;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    authService.removeAuthToken();
    dispatch({ type: AUTH_ACTIONS.AUTH_LOGOUT });
  };

  // Google login function
  const loginWithGoogle = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.AUTH_INIT });
      
      // Get a response from Google OAuth
      const response = await googleAuthService.signIn();
      
      if (response && response.success && response.token) {
        // Store the token securely
        localStorage.setItem('token', response.token);
        
        // Set token for API requests
        authService.setAuthToken(response.token);
        
        // Extract user data from response or token
        let userData;
        
        if (response.user) {
          // If response already contains user data, use it
          userData = {
            id: response.user.id,
            name: response.user.name || response.user.email?.split('@')[0],
            email: response.user.email,
            role: response.user.role || 'user',
            emailVerified: response.user.emailVerified !== false, // Default to true for Google accounts
            authProvider: 'google'
          };
        } else {
          // Otherwise extract from token or fetch from API
          try {
            // Try to decode the token first
            const decodedToken = jwtDecode(response.token);
            
            userData = {
              id: decodedToken.id || decodedToken.sub,
              name: decodedToken.name || decodedToken.email?.split('@')[0],
              email: decodedToken.email,
              role: decodedToken.role || 'user',
              emailVerified: decodedToken.emailVerified !== false, // Default to true for Google accounts
              authProvider: 'google'
            };
            
            console.log('Extracted user data from token:', userData);
          } catch (decodeError) {
            console.error('Failed to decode token, falling back to API call:', decodeError);
            
            // If token decoding fails, try fetching from API
            userData = await fetchUserData(response.token);
          }
        }
        
        // Update auth state with user data
        dispatch({
          type: AUTH_ACTIONS.AUTH_SUCCESS,
          payload: userData
        });
        
        // Add token to the response for further use
        return { ...response, userData };
      } else {
        // Even if we have response but no token, treat as an error
        throw new Error('No authentication token received from Google Sign-In');
      }
    } catch (error) {
      console.error('Google Authentication Failed:', error);
      
      // Special handling for "account not found" - don't update auth state
      // as we'll redirect to registration
      const isAccountNotFound = error.code === 'ACCOUNT_NOT_FOUND' && error.redirectToRegister;
      const isEmailNotVerified = error.code === 'EMAIL_NOT_VERIFIED';
      
      // Don't update state for these specific redirects
      if (!isAccountNotFound && !isEmailNotVerified) {
        let errorMessage = 'Google sign-in failed. Please try again.';
        
        if (error) {
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error.message) {
            errorMessage = error.message;
          }
        }
        
        dispatch({ 
          type: AUTH_ACTIONS.AUTH_ERROR, 
          payload: errorMessage
        });
      }
      
      // Always clean up token if auth failed
      localStorage.removeItem('token');
      authService.removeAuthToken();
      
      // Preserve the original error with all its properties
      throw error;
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.AUTH_INIT });
      
      // Call the API for registration
      const response = await authService.register(userData);
      
      if (response && response.token) {
        // Store token and update state
        
        const user = {
          name: userData.name,
          email: userData.email,
          authProvider: userData.authProvider || 'email' // Add auth provider information
        };
        
        dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: user });
        return { success: true };
      } else {
        throw new Error('Registration failed. Please try again.');
      }
    } catch (error) {
      // Get error message safely
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      dispatch({ 
        type: AUTH_ACTIONS.AUTH_ERROR, 
        payload: errorMessage
      });
      throw error;
    }
  };

  // Update user function
  const updateUser = async (userId, userData) => {
    try {
      const response = await axios.put(`${API_URL}/users/${userId}`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Check the response structure according to your API
      if (response.data && (response.data.success !== false)) {
        // Merge updated data with existing user state
        const updatedUser = { ...state.user, ...userData };
        
        // Update the user in context
        dispatch({ 
          type: AUTH_ACTIONS.AUTH_SUCCESS, 
          payload: updatedUser 
        });
        
        return { 
          success: true, 
          data: response.data.data || updatedUser 
        };
      } else {
        throw new Error(response.data?.message || 'Failed to update user profile');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      
      // Show specific error message if available
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to update user profile';
                          
      throw new Error(errorMessage);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        error,
        login,
        register,
        logout,
        loginWithGoogle,
        updateUser,
        checkAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};